import { Fragment } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';

import PageLayout from 'src/components/PageLayout';
import { Authentication } from 'src/lib/Authentication';
import { getQueryParams } from 'src/lib/browser';
import { stripGraphQLErrorPrefix } from 'src/lib/graphql';
import { useEffectOnce, useHistory } from 'src/lib/hooks';
import * as ls from 'src/lib/localData';
import { GOOGLE_STATE_MISMATCH_ERROR_CODE, OAUTH_POPUP_NAME } from 'src/lib/oauth';
import { captureMessage } from 'src/lib/sentry';
import { takeSignInReturnPath } from 'src/lib/signInReturnPath';
import { ROUTE_PARAMS, ROUTES } from 'src/routes';
import { useNotificationActions } from 'src/store/notification';
import { useUserActions } from 'src/store/user';

import useStyles from './useStyles';

// Another site, or an AI tool that opened the sign-in window, can also be the opener.
// Only our own pop-up with an opener on this origin sends the token to it.
const isOwnSignInPopup = () => {
  if (window.name !== OAUTH_POPUP_NAME || !window.opener || window.opener === window) {
    return false;
  }

  try {
    return window.opener.location.origin === window.location.origin;
  } catch {
    // The location of an opener on another origin is not readable.
    return false;
  }
};

const OAuthCallback = () => {
  const styles = useStyles();
  const history = useHistory();
  const userActions = useUserActions();
  const notificationActions = useNotificationActions();

  useEffectOnce(() => {
    const queryParams = getQueryParams();

    const result = {
      token: queryParams.token,
      wasUserCreated: queryParams.created_user === 'true',
      errorMessage: queryParams.error_message,
    };

    if (isOwnSignInPopup()) {
      window.opener.postMessage(result, window.location.origin);
      return;
    }

    if (result.errorMessage) {
      // A resubmitted Google page fails the state check, after an earlier callback signed the user in.
      // Keep that session and hide the error. Any other error is shown, even when a token is stored.
      const isStateMismatch = queryParams.error_code === GOOGLE_STATE_MISMATCH_ERROR_CODE;
      if (isStateMismatch && Authentication.isAuthenticated()) {
        history.replace(ROUTES.ROOT);
        return;
      }

      // The backend writes this message for the user. useErrorHandler replaces it with a generic text.
      notificationActions.displayError(new Error(result.errorMessage));
      history.replace(ROUTES.SIGN_IN);
      return;
    }

    // This window finishes the sign-in itself. It does what the sign-in page does after a pop-up.
    // navigateToApplication is not used: it keeps the query string, and here the query holds the token.
    userActions
      .loginOAuth(result)
      .then(() => {
        ls.currentWorkspaceId.remove();

        const returnPath = takeSignInReturnPath();
        if (returnPath) {
          history.replace(returnPath);
          return;
        }

        history.replace(
          result.wasUserCreated ? `${ROUTES.ROOT}?${ROUTE_PARAMS.userCreated}` : ROUTES.ROOT
        );
      })
      .catch((loginError) => {
        const message = stripGraphQLErrorPrefix(loginError);
        captureMessage(`OAuth login error: ${message}`, {
          level: 'info',
        });

        history.replace(ROUTES.SIGN_IN);
      });
  });

  return (
    <Fragment>
      <PageLayout className={styles.root}>
        <CircularProgress size={48} />
      </PageLayout>
    </Fragment>
  );
};

export default OAuthCallback;
